import ToastEventBus from 'primevue/toasteventbus'
import { reactive } from 'vue'

import { ApiError } from '@/api/http'
import { getI18n } from '@/app/i18n'

export type ToastVariant = 'success' | 'info' | 'warning' | 'danger'

/** Legacy toasts stayed 4 s (vue-toastification default, wp/shell §4.13). */
export const TOAST_LIFE = 4000

const SEVERITY: Record<ToastVariant, 'success' | 'info' | 'warn' | 'error'> = {
  success: 'success',
  info: 'info',
  warning: 'warn',
  danger: 'error',
}

export interface ToastAction {
  /** The button's text ("Undo"). */
  label: string
  run: () => void
}

export interface ToastOptions {
  /** Second line. Plain text: messages carry no HTML (I18N.md rule 4; legacy rendered HTML). */
  text?: string
  life?: number
  /**
   * One button in the toast ("Undo" after removing something). Such a toast is drawn by `<AppFeedbackHost>`'s own list
   * (same place and colours as the others, `data-kit="toast-action"`) and stays 6 s unless `life` says otherwise;
   * clicking the button runs it and closes the toast.
   */
  action?: ToastAction
}

/** How long a toast with an action stays: long enough to read it and change one's mind. */
export const ACTION_TOAST_LIFE = 6000

let hostReady = false
const pending: unknown[] = []

/**
 * Called by `<AppFeedbackHost>` once its Toast listens. A toast raised before — by a guard during the first navigation,
 * before the app is mounted (the company-profile wizard gate) — waits for it instead of being lost.
 */
export function toastHostReady(): void {
  hostReady = true
  pending.splice(0).forEach((message) => ToastEventBus.emit('add', message))
}

/** A toast with a button, as `<AppFeedbackHost>` draws it. */
export interface ActionToast {
  id: number
  variant: ToastVariant
  title: string
  text?: string
  action: ToastAction
}

/** The toasts with a button on screen, oldest first (`<AppFeedbackHost>` renders them). */
export const actionToasts = reactive<ActionToast[]>([])
let actionSeq = 0
const actionTimers = new Map<number, ReturnType<typeof setTimeout>>()

export function dismissActionToast(id: number): void {
  clearTimeout(actionTimers.get(id))
  actionTimers.delete(id)
  const index = actionToasts.findIndex((item) => item.id === id)
  if (index >= 0) actionToasts.splice(index, 1)
}

/** Runs a toast's action, then closes it. */
export function runActionToast(id: number): void {
  const item = actionToasts.find((entry) => entry.id === id)
  dismissActionToast(id)
  item?.action.run()
}

function showAction(variant: ToastVariant, title: string, options: ToastOptions & { action: ToastAction }): void {
  const id = (actionSeq += 1)
  actionToasts.push({ id, variant, title, text: options.text, action: options.action })
  actionTimers.set(
    id,
    setTimeout(() => dismissActionToast(id), options.life ?? ACTION_TOAST_LIFE),
  )
}

function show(variant: ToastVariant, title: string, options: ToastOptions = {}): void {
  if (options.action) return showAction(variant, title, { ...options, action: options.action })
  const message = {
    severity: SEVERITY[variant],
    summary: title,
    detail: options.text,
    life: options.life ?? TOAST_LIFE,
  }
  if (hostReady) ToastEventBus.emit('add', message)
  else pending.push(message)
}

/**
 * Top-right toasts (the `<AppFeedbackHost>` mounted once by the app renders them). Usable outside components — route
 * guards and the session API raise toasts too.
 */
export const toast = {
  show,
  success: (title: string, options?: ToastOptions) => show('success', title, options),
  info: (title: string, options?: ToastOptions) => show('info', title, options),
  warning: (title: string, options?: ToastOptions) => show('warning', title, options),
  danger: (title: string, options?: ToastOptions) => show('danger', title, options),
  clear: () => {
    ToastEventBus.emit('remove-all-groups')
    for (const item of [...actionToasts]) dismissActionToast(item.id)
  },
}

export function useToast(): typeof toast {
  return toast
}

/**
 * Legacy `successMessage(res)`: the server message of a success answer, else "Success". Accepts an `ApiResponse` or
 * any object with `message` (fixes P2: the fallback threw in legacy).
 */
export function successMessage(response?: { message?: string | null } | null): string {
  return response?.message || getI18n().global.t('common.state.success')
}

/**
 * Legacy `errorMessage(err)`: the server message of a refused call, else "An error has occurred". For 403/404/503 the API
 * itself answers the generic text (FRONTEND_ISSUES P8).
 */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError && error.serverMessage) return error.serverMessage
  return getI18n().global.t('common.state.error')
}
